import React, { useEffect, useContext } from "react";
import dayjs from "dayjs";

import { useCompanySettings, useSelectedInstrument } from "../hooks/useCompanySettings";
import { useDividendData, useSharesPurchased } from "../hooks/useTrades";
import { useInstrument } from "../hooks/useInstruments";
import { SharesPurchasedSelectContext } from "../context/AmountAndShareSelectContext";
import { DATE_FORMAT_PARAM } from "../constant/common";
import { getDateParam, pickBy } from "../utils";
import { arabicToNumber } from "@euroland/format-number";
import { compareObject } from "../utils/common";
import { useDispatch, useSelector } from "react-redux";
import { selectedInstrumentMinimumPriceSelector, updateUserEntryAmountInvested } from "../store/slice/trade";

const withSharesPurchasedSelection = ComponentWrapper => props => {
  const dispatch = useDispatch();
  const companySettings = useCompanySettings();
  const [{ limitStartingData, limitInvestmentStartDate }] = useSelectedInstrument();
  const dividendData = useDividendData();

  const [{ selectedInstrument }] = useInstrument();
  const [{ sharePurchased, loading, query }, { getSharesPurchased, updateQuery }] = useSharesPurchased();
  const selectedInstrumentMinimumPrice = useSelector(selectedInstrumentMinimumPriceSelector);

  const { tab, enabledDividendOption, ...formState } = useContext(SharesPurchasedSelectContext);
  const { setFieldValue, handleSubmit, getFieldValue } = formState;
  const { startingDate } = selectedInstrument;

  useEffect(() => {
    if (typeof sharePurchased.shareBought === "number" || typeof sharePurchased.shareBought === "string") {
      setFieldValue("shareNumber", sharePurchased.shareBought);
    }
    if (query.instrumentId === selectedInstrument.instrumentId && typeof query.startDate === "string") {
      setFieldValue("startDate", dayjs(query.startDate, DATE_FORMAT_PARAM).toDate());
    }
    if (query.instrumentId === selectedInstrument.instrumentId && typeof query.endDate === "string") {
      setFieldValue("endDate", dayjs(query.endDate, DATE_FORMAT_PARAM).toDate());
    }

    setFieldValue("currency", query.currency || "");

    setFieldValue("dividendChecked", !!dividendData?.name);
  }, [sharePurchased, setFieldValue, query, dividendData, tab, selectedInstrument.instrumentId]);

  useEffect(() => {
    updateQuery({ shareNumber: sharePurchased.shareBought });
  }, [sharePurchased.shareBought, query.shareNumber]);

  const onSubmit = () =>
    handleSubmit(state => {
      const params = { ...state };
      if (params.startDate) {
        params.startDate = getDateParam(params.startDate);
      }
      if (params.endDate) {
        params.endDate = getDateParam(params.endDate);
      }
      if (params.shareNumber) {
        params.shareNumber = Math.floor(
          Number(
            companySettings.useLatinNumber ? params.shareNumber : arabicToNumber(String(params.shareNumber))
          )
        );
        dispatch(updateUserEntryAmountInvested(selectedInstrumentMinimumPrice * params.shareNumber));
      }
      params.dividendChecked = enabledDividendOption && params.dividendChecked;
      params.instrumentId = selectedInstrument.instrumentId;
      // ignore amountInvested in query
      // if (compareObject({ ...params, amountInvested: query.amountInvested }, query)) return;
      getSharesPurchased(pickBy(params));
    },
    (error) => {
      document.getElementById(Object.keys(error)[0])?.focus();
    }
  );

  let minStartDate =
    (limitStartingData && new Date(limitStartingData)) || (startingDate && new Date(startingDate));
  let maxStartDate =
    (limitInvestmentStartDate && new Date(limitInvestmentStartDate)) || getFieldValue("endDate");
  let minEndDate = getFieldValue("startDate");
  let maxEndDate = new Date();

  return (
    <ComponentWrapper
      formState={formState}
      loading={loading}
      enabledDividendOption={enabledDividendOption}
      onSubmit={onSubmit}
      minStartDate={minStartDate}
      maxStartDate={maxStartDate}
      minEndDate={minEndDate}
      maxEndDate={maxEndDate}
      {...props}
    />
  );
};

export default withSharesPurchasedSelection;
